import type { FluxErrorEntityErrorCodes } from '../types.js';

export const ReviewErrorCodes = {
  ReviewAlreadyFinalized: {
    code: 'review/already-finalized',
    message: 'Não é possível alterar aprovadores de uma revisão já finalizada.',
    explanation:
      'Você não pode alterar os revisores de uma revisão que já foi finalizada. Revisões finalizadas, ou seja, aprovadas ou rejeitadas, não podem ter seus revisores alterados.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisões',
        path: '/wiki/docs/cards/approvals/intro',
      },
      {
        name: 'Pós Revisão',
        path: '/wiki/docs/cards/approvals/post-review',
      },
    ],
  },
  ReviewCommentRequired: {
    code: 'review/comment-required',
    message: 'O comentário é obrigatório para aprovar ou rejeitar.',
    explanation:
      'Um comentário é obrigatório para aprovar ou rejeitar esta revisão. Revisões podem ser configuradas para exigir comentários dos revisores.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Revisando um processo',
        path: '/wiki/docs/cards/approvals/review-card#configuração-avançada-comentário-obrigatório-para-aprovar',
      },
    ],
  },
  ReviewNotFound: {
    code: 'review/not-found',
    message: 'A aprovação solicitada não foi encontrada.',
    explanation:
      'A revisão que você está tentando acessar não foi encontrada. Ela pode ter sido concluída, cancelada ou removida.',
    localizations: {},
  },
  ReviewRequestFirstReviewerNotFound: {
    code: 'review/request-first-reviewer-not-found',
    message: 'Não foi possível encontrar o primeiro revisor.',
    explanation: 'O primeiro revisor para esta solicitação de revisão não foi encontrado.',
    localizations: {},
  },
  ReviewRequestIncompleteReviewers: {
    code: 'review/request-incomplete-reviewers',
    message: 'Não há aprovadores suficientes configurados para concluir esta solicitação. ',
    explanation:
      'Não há revisores suficientes para concluir esta solicitação de revisão. Revisões podem exigir um número mínimo de aprovadores para aprovação.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Atribuindo um revisor',
        path: '/wiki/docs/cards/approvals/assign-reviewer',
      },
    ],
  },
  ReviewRequestNotAssigned: {
    code: 'review/request-not-assigned',
    message: 'Você não está atribuído a este card e não pode solicitar uma revisão.',
    explanation:
      'Você não está atribuído a este card e, portanto, não pode solicitar uma revisão. Apenas usuários atribuídos podem solicitar revisões para seus cards.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Responsáveis pelo Card',
        path: '/wiki/docs/cards/tabs/general#responsáveis',
      },
    ],
  },
  ReviewRequestNotFound: {
    code: 'review/request-not-found',
    message: 'A solicitação de aprovação não foi encontrada.',
    explanation: 'A solicitação de revisão que você está tentando acessar não foi encontrada.',
    localizations: {},
  },
  ReviewRequestNotOwner: {
    code: 'review/request-not-owner',
    message: 'Apenas o proprietário pode gerenciar esta solicitação de aprovação.',
    explanation:
      'Você não é o proprietário desta solicitação de revisão e, portanto, não pode gerenciá-la. Apenas quem requisitou a revisão pode gerenciá-la, ou seja, adicionar ou remover revisores, cancelar a solicitação, etc.',
    localizations: {},
  },
  ReviewRequestPending: {
    code: 'review/request-pending',
    message: 'Este processo já possui uma solicitação de aprovação pendente.',
    explanation:
      'Este card já possui uma solicitação de revisão pendente. Processos só podem ter uma revisão pendente por vez. Aguarde a conclusão da aprovação atual pelos aprovadores designados ou cancele a solicitação existente antes de criar uma nova.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Aprovações',
        path: '/wiki/docs/cards/approvals/intro',
      },
    ],
  },
  ReviewRequestSelfReviewer: {
    code: 'review/request-self-reviewer',
    message: 'Você não pode revisar sua própria solicitação.',
    explanation: 'Você não pode revisar sua própria solicitação de revisão.',
    localizations: {},
  },
  ReviewRequestStageDeleted: {
    code: 'review/request-stage-deleted',
    message: 'A etapa desta aprovação foi arquivada.',
    explanation:
      'A etapa associada a esta solicitação de revisão foi arquivada. Revisões são vinculadas a etapas específicas no fluxo.',
    localizations: {},
  },
  ReviewRequestStageNotFound: {
    code: 'review/request-stage-not-found',
    message: 'Etapa da aprovação não encontrada.',
    explanation: 'A etapa associada a esta solicitação de revisão não foi encontrada.',
    localizations: {},
  },
  ReviewRequestUserNotFound: {
    code: 'review/request-user-not-found',
    message: 'Usuário relacionado à aprovação não encontrado.',
    explanation: 'O usuário associado a esta solicitação de revisão não foi encontrado.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'review'>;

export const ReviewerErrorCodes = {
  ReviewerCannotApprove: {
    code: 'reviewer/cannot-approve',
    message: 'Você não pode aprovar essa solicitação no momento. Tentar novamente mais tarde.',
    explanation:
      'Você não pode aprovar esta solicitação de revisão no momento. Isso pode acontecer caso a aprovação seja sequencial e não seja sua vez de aprovar.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Atribuindo um revisor',
        path: '/wiki/docs/cards/approvals/assign-reviewer',
      },
    ],
  },
  ReviewerCannotReject: {
    code: 'reviewer/cannot-reject',
    message: 'Você não pode rejeitar essa solicitação no momento. Tentar novamente mais tarde.',
    explanation:
      'Você não pode rejeitar esta solicitação de revisão no momento. Isso pode acontecer caso a aprovação seja sequencial e não seja sua vez de aprovar.',
    localizations: {},
  },
  ReviewerDuplicateReviewer: {
    code: 'reviewer/duplicate-reviewer',
    message: 'Este revisor já foi adicionado à solicitação.',
    explanation:
      'O revisor que você está tentando adicionar a esta solicitação de revisão já foi adicionado.',
    localizations: {},
  },
  ReviewerInvalidStatus: {
    code: 'reviewer/invalid-status',
    message: 'Não é possível alterar um aprovador que já revisou a solicitação.',
    explanation:
      'Você não pode alterar um revisor que já revisou a solicitação. Apenas revisores pendentes podem ser alterados.',
    localizations: {},
  },
  ReviewerNotFound: {
    code: 'reviewer/not-found',
    message: 'O aprovador especificado não foi encontrado.',
    explanation: 'O revisor que você está tentando acessar não foi encontrado.',
    localizations: {},
  },
  ReviewerPositionNotFound: {
    code: 'reviewer/position-not-found',
    message: 'Posição do revisor não encontrada.',
    explanation: 'A posição do revisor que você está tentando acessar não foi encontrada.',
    localizations: {},
  },
  ReviewerRequestCompleted: {
    code: 'reviewer/request-completed',
    message: 'Esta solicitação de aprovação já foi concluída.',
    explanation:
      'Esta solicitação de revisão já foi concluída e não pode ser modificada. Revisões concluídas são aquelas que foram aprovadas ou rejeitadas.',
    localizations: {},
  },
  ReviewerRequiredFieldsEmpty: {
    code: 'reviewer/required-fields-empty',
    message: 'O formulário não foi preenchido corretamente. Verifique os campos obrigatórios.',
    explanation:
      'Um ou mais campos obrigatórios no formulário do revisor não foram preenchidos. Revisões podem ter campos obrigatórios configurados.',
    localizations: {},
    supportingDocs: [
      {
        name: 'Formulários',
        path: '/wiki/docs/pipe/forms',
      },
      {
        name: 'Revisando um processo',
        path: '/wiki/docs/cards/approvals/review-card',
      },
    ],
  },
  ReviewerStatusMismatch: {
    code: 'reviewer/status-mismatch',
    message: 'O status do revisor não permite esta ação.',
    explanation: 'O status do revisor não permite que esta ação seja executada.',
    localizations: {},
  },
} as const satisfies FluxErrorEntityErrorCodes<'reviewer'>;
